import { Injectable } from '@angular/core';
import { IOption } from '@app/main/master-filter/cascade-selection.component';
import { IOwnership } from '@shared/common/common-types';
import {
    EstateCategoriesPaginatedRequestModel,
    EstateCategoriesServiceProxy, EstateTypeListingResponseModel,
    EstateTypesPaginatedRequestModel,
    EstateTypesServiceProxy,
    OwnershipStatus,
} from '@shared/service-proxies/service-proxies';
import { isNil } from 'lodash';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';


interface IEstateTypeByCategory {
    [key: string]: IOption[];
}

@Injectable({ providedIn: 'root' })
class MasterFilterService2 {

    private resetDataSubject = new Subject<void>();

    private estateCategoriesFirstLevel = new BehaviorSubject<IOption[]>(null);

    private selectedEstateCategoryFirstLevel = new BehaviorSubject<IOption>(null);

    private estateCategoriesSecondLevel = new BehaviorSubject<IOption[]>(null);

    private selectedEstateCategoriesSecondLevel = new BehaviorSubject<IOption[]>([]);

    private estateTypes = new BehaviorSubject<IOption[]>(null);

    private selectedEstateTypes = new BehaviorSubject<IOption[]>(null);

    private estateCategorySecondLevelAll: IOption[] = [];

    private estateTypesByCategory: IEstateTypeByCategory = {};

    private selectedProperties: string[] = [];

    private areaLabelSubject = new BehaviorSubject<string | null>(null);

    private priceLabelSubject = new BehaviorSubject<string | null>(null);

    private priceSquareLabelSubject = new BehaviorSubject<string | null>(null);

    // TODO: ask about these codes
    private ownershipOptions: IOwnership[] = [
        { name: 'Свободни', code: 'NY', color: '#C7EB59' },
        { name: 'Ексклузивни', code: 'RM', color: '#C24822' },
        { name: 'Мои', code: 'LDN', color: '#000000', status: OwnershipStatus.Mine },
        { name: 'Екип', code: 'IST', color: '#000000', status: OwnershipStatus.Team },
        { name: 'Обслужени', code: 'PRS', color: '#E7A90A' },
        { name: 'Архив', code: 'PRS', color: '#EDEDF0' },
        { name: 'Всички', code: 'PRS', color: '#000000', status: OwnershipStatus.All },
    ];

    private selectedOwnership = new BehaviorSubject<IOwnership | null>(null);

    constructor(
        private estateCategoriesService: EstateCategoriesServiceProxy,
        private estateTypesService: EstateTypesServiceProxy,
    ) {
        this.loadInitialData();
        // TODO: Add selected ownership option from URL
        this.selectedOwnership.next(this.ownershipOptions.find(x => x.status === OwnershipStatus.All));
    }

    getSelectedOwnership(): Observable<IOwnership> {
        return this.selectedOwnership.asObservable();
    }

    getOwnershipOptions(): Observable<IOwnership[]> {
        return of(this.ownershipOptions);
    }

    updateOwnership(ownership: IOwnership) {
        this.selectedOwnership.next(ownership);
    }

    getResetData(): Observable<void> {
        return this.resetDataSubject.asObservable();
    }

    getEstateCategoriesFirstLevel(): Observable<IOption[]> {
        return this.estateCategoriesFirstLevel.asObservable();
    }

    updateEstateCategoriesFirstLevel(newState: IOption[]): void {
        this.estateCategoriesFirstLevel.next(newState);
    }

    getEstateCategoriesSecondLevel(): Observable<IOption[]> {
        return this.estateCategoriesSecondLevel.asObservable();
    }

    updateEstateCategoriesSecondLevel(newState: IOption[]): void {
        this.estateCategoriesSecondLevel.next(newState);
    }

    getSelectedEstateCategoryFirstLevel(): Observable<IOption> {
        return this.selectedEstateCategoryFirstLevel.asObservable();
    }

    updateSelectedEstateCategoryFirstLevel(newState: IOption): void {
        this.selectedEstateCategoryFirstLevel.next(newState);
        this.resetAndUpdateSecondLevelOptions();
    }

    getSelectedEstateCategorySecondLevel(): Observable<IOption[]> {
        return this.selectedEstateCategoriesSecondLevel.asObservable();
    }

    updateSelectedEstateCategorySecondLevel(newState: IOption[]): void {
        this.selectedEstateCategoriesSecondLevel.next(newState);
        this.updateEstateTypeOptions();
    }

    getEstateTypes(): Observable<IOption[]> {
        return this.estateTypes.asObservable();
    }

    updateEstateTypes(newState: IOption[]): void {
        this.estateTypes.next(newState);
    }

    updateSelectedEstateTypes(newState: IOption[]): void {
        this.selectedEstateTypes.next(newState);
    }

    getSelectedEstateTypes(): Observable<IOption[]> {
        return this.selectedEstateTypes.asObservable();
    }


    // Advanced filters methods
    updateSelectedProperty(property: string) {
        if (!this.selectedProperties.includes(property)) {
            this.selectedProperties.push(property);
        }
    }

    removeSelectedProperty(property: string) {
        this.selectedProperties = this.selectedProperties.filter(p => p !== property);
    }

    isPropertySelected(property: string): boolean {
        return this.selectedProperties.includes(property);
    }

    clearEstateCategoriesAndTypes() {
        this.selectedEstateCategoryFirstLevel.next({} as IOption);
        this.selectedEstateCategoriesSecondLevel.next([]);
        this.selectedEstateTypes.next([]);
        this.resetDataSubject.next();
    }

    getAreaLabel(): Observable<string> {
        return this.areaLabelSubject.asObservable();
    }

    updateAreaLabel(newLabel: string) {
        this.areaLabelSubject.next(newLabel);
    }

    getPriceLabel(): Observable<string> {
        return this.priceLabelSubject.asObservable();
    }

    updatePriceLabel(newLabel: string) {
        this.priceLabelSubject.next(newLabel);
    }

    getPriceSquareLabel(): Observable<string> {
        return this.priceSquareLabelSubject.asObservable();
    }

    updatePriceSquareLabel(newLabel: string) {
        this.priceSquareLabelSubject.next(newLabel);
    }

    setEstateCategoriesAutomatically(estateCategoryName: string) {
        const estateCategory = this.estateCategorySecondLevelAll.find(x => x.label === estateCategoryName);
        if (isNil(estateCategory)) {
            return;
        }

        const parent = this.estateCategoriesFirstLevel.getValue()
            .find(x => x.id === estateCategory.parentId);

        if (isNil(parent)) {
            return;
        }

        this.updateSelectedEstateCategoryFirstLevel(parent);
        this.updateSelectedEstateCategorySecondLevel([estateCategory]);
    }

    private loadInitialData(): void {
        this.loadEstateCategories()
            .subscribe(() => {
                this.resetAndUpdateSecondLevelOptions();
            });
        this.loadEstateTypes();
    }

    private loadEstateCategories(): Observable<void> {
        const options = this.getDefaultEstateCategoriesOptions();
        return new Observable((observer) => {
            this.estateCategoriesService.getAll(options)
                .subscribe((result) => {
                    const estateCategoriesAll = result.items;

                    const { estateCategoriesFirstLevel, estateCategorySecondLevelAll } = estateCategoriesAll.reduce(
                        (acc, i) => {
                            const option = {
                                id: i.id,
                                value: i.id.toString(),
                                label: i.name,
                                parentId: i.parentId,
                            };
                            if (i.parentId === null) {
                                acc.estateCategoriesFirstLevel.push(option);
                            } else {
                                acc.estateCategorySecondLevelAll.push(option);
                            }
                            return acc;
                        },
                        { estateCategoriesFirstLevel: [], estateCategorySecondLevelAll: [] },
                    );

                    this.updateEstateCategoriesFirstLevel(estateCategoriesFirstLevel);
                    this.estateCategorySecondLevelAll = estateCategorySecondLevelAll;

                    // Notify that loading is complete
                    observer.next();
                    observer.complete();
                });
        });
    }


    private getDefaultEstateCategoriesOptions() {
        const options = new EstateCategoriesPaginatedRequestModel();
        options.pageSize = 100;
        options.page = 0;
        options.filters = [];
        options.sortBy = [];
        options.onlyActive = true;
        return options;
    }

    private getDefaultEstateTypesOptions() {
        const options = new EstateTypesPaginatedRequestModel();
        options.pageSize = 100;
        options.page = 0;
        options.filters = [];
        options.sortBy = [];
        options.onlyActive = true;
        return options;
    }

    private loadEstateTypes(): void {
        const options = this.getDefaultEstateTypesOptions();
        this.estateTypesService.getAll(options)
            .subscribe((result) => {

                const { items } = result;

                items.sort(this.customEstateTypeSorting);

                items.forEach((i, index) => {
                    if (!this.estateTypesByCategory[i.categoryId]) {
                        this.estateTypesByCategory[i.categoryId] = [];
                    }
                    this.estateTypesByCategory[i.categoryId].push({
                        id: i.id,
                        value: i.id.toString(),
                        label: i.name,
                        order: index,
                    } as IOption);
                });

            });
    }

    private resetAndUpdateSecondLevelOptions() {
        this.updateEstateTypes([]);
        const selectedFirstLevelOption = this.selectedEstateCategoryFirstLevel.getValue();

        if (isNil(selectedFirstLevelOption)) {
            this.updateEstateCategoriesSecondLevel([]);
            return;
        }

        const newSecondLevel = this.estateCategorySecondLevelAll.filter((i) => i.parentId === selectedFirstLevelOption.id);
        this.updateEstateCategoriesSecondLevel(newSecondLevel);
    }

    private updateEstateTypeOptions() {
        const selectedSecondLevelOptions = this.selectedEstateCategoriesSecondLevel.getValue();

        const newEstateTypes = [];
        selectedSecondLevelOptions?.forEach((i) => {
            this.estateTypesByCategory[i.id].forEach((j) => {
                newEstateTypes.push(j);
            });
        });

        newEstateTypes.sort(this.customIOptionSorting);

        this.updateEstateTypes(newEstateTypes);
    }

    private customEstateTypeSorting(a: EstateTypeListingResponseModel, b: EstateTypeListingResponseModel): number {
        if (a.categoryId < b.categoryId) {
            return -1;
        }
        if (a.categoryId > b.categoryId) {
            return 1;
        }
        if (a.id < b.id) {
            return -1;
        }
        if (a.id > b.id) {
            return 1;
        }
        return 0;
    }

    private customIOptionSorting(a: IOption, b: IOption): number {
        if (a.order < b.order) {
            return -1;
        }
        if (a.order > b.order) {
            return 1;
        }
        return 0;
    }
}

export default MasterFilterService2;
